import { AutomationMessageApi } from '@app/api';
import {
  AutomationMessageSourceText, AutomationMessageTypeIcon
} from '@app/components/AutomationMessageIcons';
import { JsonViewer } from '@app/components/JsonViewer';
import {
  AutomationMessage, TYPE_RANK, compareSource, isKlassMessage, loudestType, sourceOf
} from '@app/model/AutomationMessage';
import { AdHocKlass, Klass, PrivateKlass } from '@app/model/Klass';
import { ddMMyyyyHHmmFormat } from '@app/utils/functions';
import {
  Button, Divider, ExpandableSection, Flex, FlexItem, Modal, ModalBody, ModalHeader, ModalVariant,
  Stack, StackItem, Title, Tooltip
} from '@patternfly/react-core';
import * as React from 'react';

/** Secondary text - the stamps under a message, the same shade the messages panel gives them. */
const subtleText = 'var(--pf-t--global--text--color--subtle, #6a6e73)';

/**
 * Every key an automation message about this class could be filed under.
 *
 * Two of them for a class that came in as a request. The importing systems file a finding under the
 * id they hold the class by, and that is not the same id for all of them: an offering id once the
 * class exists in the LMS, but the request id - `REQnnnn` for a private class, `ADRnnnn` for an ad
 * hoc one - for whatever was reported about the request that became it. Asking for both is what
 * makes a private class's messages reachable from the class rather than only from the panel.
 *
 * Empty entries are dropped rather than sent: a class whose offering id is not set yet would
 * otherwise ask the service for messages filed under nothing and get somebody else's.
 */
const automationKeys = (klass: Klass): string[] => {
  const requestId = (klass as PrivateKlass | AdHocKlass).requestId;
  return [klass.offeringId, requestId]
    .map(value => (value === null || value === undefined ? '' : String(value).trim()))
    .filter(value => value !== '' && value !== '0');
};

/**
 * The messages a class holds, loudest first and then in source order - the ERROR that stops the
 * class being usable is what the reader opened the modal for, and the sheet's findings before this
 * service's within one severity, being the ones fixed furthest upstream.
 */
const inReadingOrder = (messages: AutomationMessage[]): AutomationMessage[] =>
  [...messages].sort((a, b) =>
    (TYPE_RANK[b.type] ?? -1) - (TYPE_RANK[a.type] ?? -1)
    || compareSource(sourceOf(a), sourceOf(b))
    || (a.key ?? '').localeCompare(b.key ?? '', undefined, { numeric: true })
  );

/**
 * The message's payload as the viewer wants it. Shown as it came when it is not the JSON it is
 * meant to be: the point of showing it at all is to see what the import saw, and an unparsable
 * payload is itself worth seeing.
 */
const parsedValue = (value: string): unknown => {
  if (!value) return 'No data';
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * What the imports had to report about one class, fetched as the page opens it.
 *
 * A hook for the page rather than a fetch inside the component below, because the component sits in
 * the title bar and the title bar is rendered through a portal: DetailPageHeader finds the layout's
 * slot in a layout effect, so its whole subtree is thrown away and mounted again the moment the
 * slot arrives. An effect living down there therefore runs twice on every class opened, and asks
 * the service twice for the same two keys. Called from the page, which does not remount, it runs
 * once - so keep this where the record is loaded and pass the messages down.
 *
 * Answers with nothing while the read is in flight, and with nothing if it fails.
 */
export const useKlassAutomationMessages = (klass: Klass): AutomationMessage[] => {
  const [messages, setMessages] = React.useState<AutomationMessage[]>([]);

  // Joined, so the effect below re-runs when the class the page is showing changes but not on
  // every re-render of one that has not: automationKeys builds a new array each time it is called,
  // and an array in a dependency list is a new dependency on each render.
  const keyList = automationKeys(klass).join(',');

  React.useEffect(() => {
    const wanted = keyList ? keyList.split(',') : [];
    if (wanted.length === 0) {
      setMessages([]);
      return;
    }

    // The class can change under a fetch already in flight - a Refresh, or the next class opened
    // from a link on this one - and the slower answer must not land on the newer class.
    let current = true;

    void (async () => {
      try {
        const answers = await Promise.all(wanted.map(key => AutomationMessageApi.getByKey(key)));
        if (!current) return;

        // Deduplicated by id because the keys are asked for separately: nothing files one message
        // under two keys today, but two requests are two answers and a repeated row would be
        // counted twice on the badge.
        const byId = new Map<number | string, AutomationMessage>();
        answers.flat().filter(isKlassMessage).forEach(msg => byId.set(msg.id, msg));
        setMessages(inReadingOrder(Array.from(byId.values())));
      } catch {
        // Deliberately quiet. This is a side note about a record the page is already showing, and
        // a toast for a read the user did not ask for would sit on top of the class they came to
        // work on. fetchData has already told the health store, so an outage is still reported -
        // by the banner that reports every other one, in one place.
        if (current) setMessages([]);
      }
    })();

    return () => { current = false; };
  }, [keyList]);

  return messages;
};

interface KlassAutomationMessagesProps {
  /** The class the messages are about, named in the modal so a reader knows what they opened. */
  offeringId: number;
  /** What useKlassAutomationMessages answered with. */
  messages: AutomationMessage[];
}

/**
 * The warning sign in a class's title bar saying the imports had something to report about it, and
 * the modal that reads out what.
 *
 * These messages have only ever been reachable from Administration > Automation Messages, which is
 * a log of every class at once: somebody working one class had no way of knowing that the class in
 * front of them is the one whose facility did not resolve. So the class says so itself, and the
 * modal is the panel's rows for this one class - the same severity and source icons, so a message
 * read here and there reads as one finding.
 *
 * Renders nothing at all when the class has no messages, which is the normal case: an icon that is
 * always there says nothing by being there, and the whole point of this one is that it is unusual.
 * Nothing is rendered while they are still being fetched either, so the title bar does not reflow a
 * beat after the page settles.
 */
export const KlassAutomationMessages: React.FC<KlassAutomationMessagesProps> = ({ offeringId, messages }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const loudest = loudestType(messages);
  if (messages.length === 0 || !loudest) {
    return null;
  }

  const count = messages.length;
  const countedMessages = `${count} automation ${count === 1 ? 'message' : 'messages'}`;

  return (
    <React.Fragment>
      <Tooltip content={`The imports reported ${countedMessages} about this class. Open to read ${count === 1 ? 'it' : 'them'}.`}>
        <Button
          variant="plain"
          id="automation-messages-button"
          aria-label={`Automation messages (${count})`}
          onClick={() => setIsOpen(true)}
          icon={<AutomationMessageTypeIcon type={loudest} />}
        >
          {/* The number is worth having beside the icon: a class carries one message per reporting
              system at most, so two means both of them found something and the modal holds two
              different problems rather than one restated. Written as the flag summary next to it
              writes its own counts - the two sit in one row, and one idiom for "this many of these"
              reads as one row rather than as two conventions. */}
          <strong>{count}</strong>
        </Button>
      </Tooltip>

      <Modal
        variant={ModalVariant.medium}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="automation-messages-title"
      >
        <ModalHeader>
          <Title headingLevel="h1" size="xl" id="automation-messages-title"
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AutomationMessageTypeIcon type={loudest} />
            Automation messages
          </Title>
        </ModalHeader>
        <ModalBody>
          <p style={{ color: subtleText, marginBottom: '16px' }}>
            What the imports could not make sense of about class {offeringId}. Each message stays
            until the next import pass reads the corrected data.
          </p>
          <Stack hasGutter>
            {messages.map((msg, index) => (
              <StackItem key={msg.id}>
                {index > 0 && <Divider style={{ marginBottom: '16px' }} />}
                <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                  <FlexItem>
                    <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}
                      flexWrap={{ default: 'nowrap' }}>
                      <FlexItem><AutomationMessageTypeIcon type={msg.type} /></FlexItem>
                      <FlexItem><strong>{msg.type}</strong></FlexItem>
                    </Flex>
                  </FlexItem>
                  <FlexItem><AutomationMessageSourceText source={sourceOf(msg)} /></FlexItem>
                  {/* Which id this was filed under, named rather than assumed: a private class's
                      findings arrive under its request id, so a reader seeing a REQ number here is
                      seeing why the message is on this class at all. */}
                  <FlexItem style={{ color: subtleText }}>{msg.entity} {msg.key}</FlexItem>
                </Flex>
                {/* Kept as sent: the service writes these as a heading and a dash-led list of what
                    needs attention, which is lines and not a paragraph. */}
                <div style={{ whiteSpace: 'pre-line', margin: '8px 0' }}>{msg.message}</div>
                <div style={{ color: subtleText, fontSize: '0.875rem' }}>
                  {/* Both stamps, because they answer different questions: when the problem was
                      first reported, and whether it is still being reported. A message with
                      neither predates the columns - see AutomationMessage.dateCreated. */}
                  {msg.dateCreated && <span>First reported {ddMMyyyyHHmmFormat(msg.dateCreated)}</span>}
                  {msg.dateCreated && msg.dateUpdated && <span>{' · '}</span>}
                  {msg.dateUpdated && <span>last reported {ddMMyyyyHHmmFormat(msg.dateUpdated)}</span>}
                </div>
                {/* The row the import read, collapsed. It is what says which value the message is
                    about, and it is already here - the message carries it - so it costs a click
                    rather than a request. */}
                <ExpandableSection toggleText="Imported data" isIndented>
                  <JsonViewer data={parsedValue(msg.value)} initiallyExpanded />
                </ExpandableSection>
              </StackItem>
            ))}
          </Stack>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};
